import React from 'react';
import { Leaf, PackageCheck, Truck } from 'lucide-react';
import HeroSearch from './HeroSearch';
import DeliveryLocation from './DeliveryLocation';
import LocationAvailabilityPrompt from './LocationAvailabilityPrompt';
import { homeRail } from '../styles/ui';

// Floating trust panel. Wording is the site's own existing benefit language —
// no certification, guarantee or "100% safe" claim is introduced.
const TRUST_ROWS = [
  { icon: PackageCheck, title: 'Hygienically Packed', subtitle: "For your family's safety" },
  { icon: Leaf, title: 'Farm Fresh', subtitle: 'Quality you can see' },
  { icon: Truck, title: 'Fast & Safe', subtitle: 'Delivery to your door' },
];

// First viewport of the approved Home design. The grocery scene on the right
// (and its handwritten notes and freshness roundel) is the design's own
// artwork, cropped from the reference into /home/hero-scene.webp; everything
// the customer acts on — search, quick chips, the location prompt and the trust
// panel's words — stays HTML.
const Hero = () => (
  <section
    id="home"
    className="relative overflow-hidden pt-20 bg-[linear-gradient(180deg,#FFFFFF_0%,#F7FBF5_60%,#F3F9F1_100%)]"
  >
    <div className="pointer-events-none select-none absolute inset-x-0 top-20 bottom-0" aria-hidden="true">
      <img
        src="/home/decor-hero-left.webp"
        alt=""
        width="150"
        height="362"
        className="hidden xl:block absolute left-0 top-0 h-full w-auto"
      />
    </div>

    <div className={homeRail}>
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-4 xl:min-h-[354px]">
        {/* Copy column — 467px wide from x=163 in the 1536px reference. */}
        <div className="relative z-10 w-full lg:w-[44%] xl:w-[467px] shrink-0 pt-6 pb-4 lg:pb-6 xl:ml-[67px]">
          <DeliveryLocation className="lg:hidden -ml-2 mb-3" />

          <span className="inline-flex items-center gap-1.5 h-6 px-3 rounded-full bg-white border border-emerald-100 text-emerald-800 text-[10.5px] font-bold tracking-[0.04em] uppercase shadow-[0_1px_3px_rgba(15,51,24,0.08)]">
            <Leaf size={12} className="text-emerald-600" aria-hidden="true" />
            Fresh, Hygienic &amp; Handpicked Daily
          </span>

          <h1 className="serif mt-[15px] text-[38px] sm:text-[46px] xl:text-[54px] font-bold leading-[1.02] tracking-[-0.01em] text-emerald-950">
            Good Food,
            <br />
            <span className="text-[#1E8A3B]">Happier Homes</span>
          </h1>

          <p className="mt-[10px] max-w-[362px] text-[15px] leading-[20px] text-[#4B5563]">
            Premium quality meat, seafood, eggs, vegetables, fruits, dairy and daily essentials —
            delivered fresh to your doorstep.
          </p>

          <LocationAvailabilityPrompt className="max-w-md mt-3" />

          <div className="mt-[12px]">
            <HeroSearch />
          </div>
        </div>

        {/* Scene column. Below xl the artwork flows in the grid; at xl it is
            pinned to the viewport's right edge at the reference's size. */}
        <div className="relative flex-1 min-w-0 lg:self-end xl:static">
          <img
            src="/home/hero-scene.webp"
            alt="Fresh & Homely groceries: vegetables, chicken, fish, mutton, eggs and milk around a branded paper bag"
            width="976"
            height="362"
            className="block w-full max-w-[976px] mx-auto h-auto xl:absolute xl:right-0 xl:top-20 xl:mx-0 xl:w-[976px] xl:max-w-none"
          />

          {/* Trust panel floating over the artwork (x 1262–1448, y 85–222). */}
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 xl:mt-0 xl:absolute xl:right-[88px] xl:top-[107px] xl:w-[186px] xl:grid-cols-1 xl:gap-0 xl:bg-white/95 xl:backdrop-blur xl:rounded-xl xl:shadow-[0_10px_30px_-10px_rgba(15,51,24,0.25)] xl:border xl:border-emerald-50 xl:px-3 xl:py-2.5 xl:z-20">
            {TRUST_ROWS.map(({ icon: Icon, title, subtitle }) => (
              <li
                key={title}
                className="flex items-center gap-2.5 rounded-xl bg-white/95 border border-emerald-100 xl:border-0 xl:bg-transparent xl:rounded-none px-3 py-2 xl:px-0 xl:py-[7px]"
              >
                <Icon size={20} className="text-emerald-700 shrink-0" strokeWidth={1.9} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[12px] font-bold text-emerald-950 leading-tight truncate">{title}</span>
                  <span className="block text-[10.5px] text-ink-secondary leading-tight truncate">{subtitle}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

export default Hero;
